import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import api, { AppLocale } from '../lib/api'

interface LocaleContextValue {
  locale: AppLocale
  isRTL: boolean
  setLocale: (locale: AppLocale) => Promise<void>
  tx: (en: string, he: string) => string
}

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined)
const STORAGE_KEY = 'quor_locale'

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<AppLocale>(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'en' || stored === 'he') return stored
    return 'en'
  })

  useEffect(() => {
    api.setLocale(locale)
    localStorage.setItem(STORAGE_KEY, locale)
    document.documentElement.lang = locale
    document.documentElement.dir = locale === 'he' ? 'rtl' : 'ltr'
  }, [locale])

  useEffect(() => {
    const handleSync = () => {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored === 'en' || stored === 'he') {
        setLocaleState(stored)
      }
    }
    window.addEventListener('quor-locale-sync', handleSync)
    return () => window.removeEventListener('quor-locale-sync', handleSync)
  }, [])

  const value = useMemo<LocaleContextValue>(() => ({
    locale,
    isRTL: locale === 'he',
    setLocale: async (next: AppLocale) => {
      setLocaleState(next)
      try {
        await api.updateProfile({ language: next })
      } catch {
        // Best-effort server sync; local preference still applied.
      }
    },
    tx: (en: string, he: string) => (locale === 'he' ? he : en),
  }), [locale])

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

export function useLocale() {
  const context = useContext(LocaleContext)
  if (!context) {
    throw new Error('useLocale must be used within LocaleProvider')
  }
  return context
}

